import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from 'lodash/noop';
import { v4 as getUUID } from 'uuid';

import { EditorState, convertToRaw } from 'draft-js';
import Editor from 'draft-js-plugins-editor';
import createMentionPlugin from 'draft-js-mention-plugin';

import StickyHeaderListMentionAdapter from './StickyHeaderListMentionAdapter';
import MentionSuggestionEntry from './MentionSuggestionEntry';

import positionSuggestions from '@adsk/bim360-matrix-react-helpers/es/positionSuggestions';

import './_MentionInput.scss';

/**
 * @class MentionInput
 **/
class MentionInput extends Component {
  constructor(props) {
    super(props);

    this.MENTION_PREFIX = '@';
    this.MENTION_TRIGGER = '@';

    const suggestions = this.buildSuggestions(props);

    this.state = {
      editorState: props.editorState || EditorState.createEmpty(),
      suggestions,
      focused: false,
      remount: false
    };

    this.setMentionPlugin(props);

    this._handleKeyUp = this._handleKeyUp.bind(this);
  }

  setMentionPlugin(props) {
    this.editorKey = getUUID();
    this.mentionPlugin = createMentionPlugin({
      mentionPrefix: this.MENTION_PREFIX,
      positionSuggestions: positionSuggestions(this.editorKey),
      theme: {
        mention: 'MentionInput__mention',
        ...props.menuAdapter.theme
      }
    });
  }

  buildSuggestions(props) {
    return props.menuAdapter.buildSuggestions(
      props.suggestions,
      props.getSuggestionName,
      props.getSuggestionId
    );
  }

  onEscape() {
    if (!this.state.viewingSuggestions) {
      // For some reason calling `this.blur()` here prevents the dispatch of 'keyup' event, which should happen by design.
      // So on escape (which is actually triggered on 'keydown') we just flag that we want to trigger blur.
      // The actual blurring occurs in the `_handleKeyUp` event handler in this components.
      this.triggerBlur = true;
    }
  }

  _handleKeyUp(event) {
    if (this.triggerBlur) {
      this.blur();
      this.triggerBlur = false;
    }
    this.props.onKeyUp(event);
  }

  componentDidMount() {
    this.mentionInputRef.addEventListener('keyup', this._handleKeyUp);
  }

  componentWillUnmount() {
    this.mentionInputRef.removeEventListener('keyup', this._handleKeyUp);
  }

  componentDidUpdate() {
    if (this.state.remount) {
      this.setState({ remount: false });
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.menuAdapter !== nextProps.menuAdapter) {
      this.resetMentionPlugin(nextProps);
    }
  }

  resetMentionPlugin(props, extraState = {}) {
    this.setMentionPlugin(props);
    this.setState({ remount: true, ...extraState });
  }

  /**
   * Used by ref from rendering component to clear the current editsor session (e.g clearing text after submitting data)
   */
  clearEditor() {
    this.resetMentionPlugin(this.props, { editorState: EditorState.createEmpty() });
  }

  onChange(editorState) {
    const rawData = convertToRaw(editorState.getCurrentContent());
    // What are we doing here???
    // The structure of rawData is as follows:
    // {
    //   entityMap: {
    //     0: {
    //       data: { mention: ... }
    //     },
    //     1: {
    //       data: { mention: ... }
    //     }
    //   },
    //   blocks: [{
    //     text: '...',
    //     entityRanges: [{
    //       key: 0,
    //       offset: ...,
    //       length: ...
    //     }]
    //   }, {
    //     text: '...',
    //     entityRanges: [{
    //       key: 1,
    //       offset: ...,
    //       length: ...
    //     }]
    //   }]
    // }
    // Where each "key" in an "entityRange" of each block corresponds to a unique key in the "entityMap"!
    // If you'll look closely, you'll see that each "entityRange" contains "offset" and "length" that corresponds to that block text.
    // Meaning that entity begins at offset X and it's Y long in the block text.
    // The problem start when we want to map each "entity" in the text to a different value as a change,
    // we want to save mention "@ori" as "@("type":"User","id":"123","name":"ori"}" where 123 is the key of that mention.
    // Why is that a problem?
    // If we'll immediately replace the contents of the block text with this value we'll be out of sync with the "offset" and "length" in the original text.
    //
    // So what do we actually do?
    // We go over each block on all of its entity ranges and we aggregate the block text like so:
    // for each entity range we add the text between the previous entity range and the current one and append the formatted mention.
    // Then we append the text between the last entity range and the end of the block text (remaining text that doesn't contain mentions)
    //
    // For example, given the string "this is just some mention @Ori Riner has done, FYI @Daniel Gefen please note the change."
    // This is how the iteration of the text will be:
    // "this is just some mention @{"type":"User", "id":"123", "name":"Ori Riner" }" ->
    // "this is just some mention @{"type":"User", "id":"123", "name":"Ori Riner" } has done, FYI @{"type":"User", "id":"456", "name":"Daniel Gefen" }" ->
    // "this is just some mention @{"type":"User", "id":"123", "name":"Ori Riner" } has done, FYI @{"type":"User", "id":"456", "name":"Daniel Gefen" } please note the change."

    const changedText = rawData.blocks
      .map(block => {
        let lastIndexOfEntity = 0;
        const blockText = block.entityRanges.reduce((blockText, entityRange) => {
          const entity = rawData.entityMap[entityRange.key];
          if (entity.type !== 'mention') {
            return blockText;
          }
          const formattedMention = this.props.mentionFormatter(
            entity.data.mention.toJS(),
            this.props
          );
          const textBetweenPreviousAndCurrentMention = block.text.slice(
            lastIndexOfEntity,
            entityRange.offset
          );

          blockText += textBetweenPreviousAndCurrentMention + formattedMention;
          lastIndexOfEntity = entityRange.offset + entityRange.length;

          return blockText;
        }, '');

        return blockText + block.text.slice(lastIndexOfEntity);
      })
      .join('\n');

    this.props.onChange(changedText, editorState);
    this.setState({ editorState });
  }

  onSearchChange({ value }) {
    const searchValue = value.toLowerCase();
    const allSuggestions = this.buildSuggestions(this.props);
    const suggestions = !searchValue
      ? allSuggestions
      : allSuggestions.filter(
        suggestion => suggestion['name'].toLowerCase().indexOf(searchValue) > -1
      );

    this.setState({ suggestions });
  }

  onFocus() {
    this.setState({ focused: true });
    this.props.onFocus();
  }

  onBlur() {
    this.setState({ focused: false });
    this.props.onBlur();
  }

  // Same as TextFieldCore
  // Making sure that we ensure a state change has occurred on the text field.
  _ensureState(forceState) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        forceState();
      });
    });
  }

  focus() {
    this.editor.focus();
    this._ensureState(() => !this.state.focused && this.onFocus());
  }

  blur() {
    this.editor.blur();
    this._ensureState(() => this.state.focused && this.onBlur());
  }

  /**
   * Render
   * @returns {VDOM}
   **/
  render() {
    const MenuAdapter = this.props.menuAdapter;
    const MentionSuggestions = this.mentionPlugin.MentionSuggestions;
    const containerClasses = classNames(this.props.className, 'MentionInput__container');

    const editorContainerClasses = classNames('MentionInput__container-editor', {
      'MentionInput__container-editor--focused': this.state.focused
    });

    return (
      <div
        onClick={this.focus.bind(this)}
        className={containerClasses}
        style={this.props.style}
        ref={_ => (this.mentionInputRef = _)}
      >
        <div className={editorContainerClasses}>
          {!this.state.remount && (
            <Editor
              editorState={this.state.editorState}
              onChange={this.onChange.bind(this)}
              plugins={[this.mentionPlugin]}
              onEscape={this.onEscape.bind(this)}
              onFocus={this.onFocus.bind(this)}
              onBlur={this.onBlur.bind(this)}
              ref={element => {
                this.editor = element;
              }}
              placeholder={this.props.placeholder}
              editorKey={this.editorKey}
              spellCheck={this.props.spellCheck}
            />
          )}
        </div>
        {!this.props.disableMention && (
          <MentionSuggestions
            popoverComponent={<MenuAdapter />}
            onSearchChange={this.onSearchChange.bind(this)}
            suggestions={this.state.suggestions}
            entryComponent={this.props.entryComponent}
            onOpen={() => this.setState({ viewingSuggestions: true })}
            onClose={() => this.setState({ viewingSuggestions: false })}
          />
        )}
      </div>
    );
  }
}

MentionInput.defaultProps = {
  mentionFormatter: (mention, mapping) =>
    `@{"type": "${mapping.getMentionType(mention).toLowerCase()}", "id": "${mapping.getMentionId(
      mention
    )}", "name": "${mapping.getMentionName(mention)}"}`,
  onChange: noop,
  onFocus: noop,
  onBlur: noop,
  onKeyUp: noop,
  getSuggestionName: suggestion => suggestion.label,
  getSuggestionId: suggestion => suggestion.eventKey,
  getMentionName: mention => mention.label,
  getMentionType: mention => mention.type,
  getMentionId: mention => mention.eventKey,
  menuAdapter: StickyHeaderListMentionAdapter,
  entryComponent: MentionSuggestionEntry,
  spellCheck: true,
  disableMention: false
};

MentionInput.propTypes = {
  /**
   * EditorState object to initialize the editor with. Useful, for instance, if the user has filled data in the MentionInput, switched tabs and returned and you want to keep the mentions.
   * onChange function supplies with the editorState for you to store it and pass it back when needed. (only available in the constructor, does not override if component is already mounted)
   */
  editorState: PropTypes.object,
  /**
   * The list of suggestions to display when triggering a mention.
   * This array should have a structure that the `menuAdapter` knows how to handle.
   */
  suggestions: PropTypes.array.isRequired,
  /**
   * class name to set on the input.
   */
  className: PropTypes.string,
  /**
   * style to set on the input
   */
  style: PropTypes.object,
  /**
   * callback that's called every time the value has changed.
   * It's called with 1) the text of the input as a parameter, where each mention has been replaced by the format and 2) The editorState (needed for when you want to mount the component with mentions)
   */
  onChange: PropTypes.func,
  /**
   * Selector function to get the name to display in the suggestion.
   */
  getSuggestionName: PropTypes.func,
  /**
   * Selector function to get the id to be as the key when rendering the suggestion list.
   */
  getSuggestionId: PropTypes.func,
  /**
   * Selector function to get the name when generating the mention text.
   */
  getMentionName: PropTypes.func,
  /**
   * Selector function to get the id when generating the mention text.
   */
  getMentionId: PropTypes.func,
  /**
   * Selector function to get the type when generating the mention text.
   */
  getMentionType: PropTypes.func,
  /**
   * Formatter function that generates the mention text according to the mention and mention selector functions above.
   */
  mentionFormatter: PropTypes.func,
  /**
   * The menu adapter component.
   * The component **MUST** have a static function called `buildSuggestions` that takes the suggestions and the suggestions selector functions above.
   * The component **MAY** have a static property called `theme` that defines custom className for the internal mention objects.
   * See "Configuration Parameters" section in https://www.draft-js-plugins.com/plugin/mention for more information.
   */
  menuAdapter: PropTypes.func,
  /**
   * A component used for displaying a single entry in the suggestion list.
   */
  entryComponent: PropTypes.func,
  /**
   * A placeholder for the input when there's no text.
   */
  placeholder: PropTypes.string,
  /**
   * Called when the input is being focused
   */
  onFocus: PropTypes.func,
  /**
   * Called when the input is being blurred
   */
  onBlur: PropTypes.func,
  /**
   * Called when a 'keyup' event has been called on the input.
   */
  onKeyUp: PropTypes.func,
  /**
   * Flag indicating whether or not the input will check spelling (using browser's implementation).
   */
  spellCheck: PropTypes.bool,
  /**
   * Whether to disable the display of the Mention.
   */
  disableMention: PropTypes.bool
};

export default MentionInput;
